import { BaseTransformer } from '../BaseTransformer';
import { TransformationUtils } from '../TransformationUtils';
import { TransformationContext } from '../types';

/**
 * Common interface for database rows with standard audit fields
 */
export interface DatabaseRow {
  id: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

/**
 * Base database transformer providing common database-to-frontend transformations
 * Handles snake_case to camelCase conversion and standard audit fields
 */
export abstract class DatabaseTransformer<TInput extends DatabaseRow, TOutput> extends BaseTransformer<TInput, TOutput> {
  protected performTransformation(input: TInput, context?: TransformationContext): TOutput {
    // Apply field transformations
    const baseOutput = this.applyFieldTransformations(input, context);
    
    // Apply common database transformations
    const output = {
      ...baseOutput,
      id: input.id,
      createdAt: TransformationUtils.toISODate(input.created_at),
      updatedAt: TransformationUtils.toISODate(input.updated_at)
    };

    // Perform entity-specific transformations
    return this.performEntityTransformation(output, input, context);
  }

  /**
   * Perform entity-specific transformation logic
   * Subclasses should override this method
   */
  protected abstract performEntityTransformation(
    baseOutput: Record<string, unknown>, 
    input: TInput, 
    context?: TransformationContext
  ): TOutput;

  /**
   * Helper to safely transform snake_case object to camelCase
   */
  protected transformToCamelCase<T>(obj: Record<string, unknown>): T {
    return TransformationUtils.objectSnakeToCamel<T>(obj);
  }

  /**
   * Helper to safely parse JSON fields with default
   */
  protected parseJsonField<T>(value: unknown, defaultValue: T): T {
    return TransformationUtils.parseJson(value as string | null | undefined, defaultValue) ?? defaultValue;
  }
}